import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Send, Pause, Play, X as XIcon, Trash2, ArrowLeft, Mail, Clock, AlertTriangle, Eye, MousePointerClick,
  ChevronDown, FileText,
} from "lucide-react";
import CampaignEditor from "./CampaignEditor";
import { formatApiError, mailingApi } from "./api";

// Content fields a template shares with a campaign (everything except the
// campaign's own name, recipients, attachments, status, and stats).
const TEMPLATE_CONTENT_FIELDS = [
  "subject", "preheader", "hero_headline", "hero_subheadline",
  "hero_image_url", "intro_paragraph", "bullets", "cta_label", "cta_url",
];

function templateContent(t) {
  const out = {};
  for (const k of TEMPLATE_CONTENT_FIELDS) {
    if (t[k] !== undefined && t[k] !== null) out[k] = t[k];
  }
  return out;
}

const STATUS_BADGE = {
  draft: "bg-[#E0D4B6]/30 text-[#3D1F00]/70 border-[#E0D4B6]",
  scheduled: "bg-[#D4A017]/40 text-[#D4A017] border-[#D4A017]",
  sending: "bg-[#E8943A]/20 text-[#7B3F00] border-[#E8943A]/60 animate-pulse",
  paused: "bg-[#D4A017]/40 text-[#D4A017] border-[#D4A017]",
  completed: "bg-[#E0D4B6]/40 text-[#4A6741] border-[#E0D4B6]",
  cancelled: "bg-[#C0392B]/40 text-[#C0392B] border-[#C0392B]",
  failed: "bg-[#C0392B]/40 text-[#C0392B] border-[#C0392B]",
};

const DEFAULT_CAMPAIGN = {
  name: "Premium export introduction",
  subject: "{{name}}, Indian rice and spice supply from Vilora Impex",
  preheader: "A short export note from Gujarat with catalogue and documents.",
  hero_headline: "Indian rice, spice and dry goods, prepared for serious buyers.",
  hero_subheadline: "Vilora Impex ships from Gujarat with lot-level checks, export paperwork, and port-ready timelines.",
  intro_paragraph: "Hello {{name}}, I'm Darshit from Vilora Impex. If {{company}} is reviewing Indian sourcing for {{country}}, I can share current availability across long-grain rice, spices, dry fruits, pulses, and fresh produce.",
  bullets: [
    { title: "Documents ready", body: "FSSAI/APEDA-aligned paperwork, certificate of origin, lab reports, and shipment photos." },
    { title: "Buyer-fit packing", body: "Retail, wholesale, private label, and bulk formats planned against your port and MOQ." },
    { title: "Port discipline", body: "Mundra-led routing, live status updates, and clear handover until arrival sign-off." },
  ],
  cta_label: "View catalogue",
  cta_url: "https://www.viloraimpex.com",
  attachments: [],
  recipient_query: { all_active: true },
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showTplMenu, setShowTplMenu] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await mailingApi.campaigns.list()); }
    catch { toast.error("Failed to load campaigns"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    mailingApi.templates.list().then(setTemplates).catch(() => {});
  }, []);

  // template = null -> blank default; otherwise start from the saved template.
  const create = async (template = null) => {
    setShowTplMenu(false);
    try {
      const base = template
        ? {
            ...DEFAULT_CAMPAIGN,
            ...templateContent(template),
            name: template.name ? `${template.name} — campaign` : "New campaign",
            attachments: template.attachments || [],
            recipient_query: { all_active: true },
          }
        : DEFAULT_CAMPAIGN;
      const c = await mailingApi.campaigns.create(base);
      setItems((cur) => [c, ...cur]);
      setEditing(c);
    } catch (err) { toast.error(formatApiError(err, "Create failed")); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await mailingApi.campaigns.remove(c.id);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
    } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
  };

  if (editing) {
    return <CampaignDetail id={editing.id} onClose={() => { setEditing(null); load(); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="velora-micro text-[#3D1F00]/50">{items.length} campaigns</div>
        <div className="flex items-center gap-2 relative">
          <div className="relative">
            <button
              onClick={() => setShowTplMenu((v) => !v)}
              className="velora-btn-ghost flex items-center gap-2"
            >
              <FileText size={14}/> From template <ChevronDown size={13}/>
            </button>
            {showTplMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowTplMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 bg-[#FFFFFF] border border-[#E0D4B6] shadow-lg min-w-[260px] max-h-[320px] overflow-y-auto">
                  {templates.length === 0 ? (
                    <div className="px-4 py-3 velora-micro text-[#3D1F00]/40">
                      No templates yet — create one in the Templates tab.
                    </div>
                  ) : (
                    templates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => create(t)}
                        className="block w-full text-left px-4 py-2.5 hover:bg-[#FAF3E0] border-b border-[#E0D4B6]/40 last:border-b-0"
                      >
                        <div className="text-sm text-[#3D1F00] truncate">{t.name}</div>
                        <div className="velora-micro text-[#3D1F00]/45 truncate">{t.subject}</div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <button onClick={() => create(null)} className="velora-btn-primary flex items-center gap-2">
            <Plus size={14}/> New campaign
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-12 text-center velora-micro text-[#3D1F00]/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-12 border border-[#E0D4B6] bg-[#FFFFFF] text-center velora-micro text-[#3D1F00]/50">
          No campaigns yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <CampaignRow key={c.id} c={c} onOpen={() => setEditing(c)} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignRow({ c, onOpen, onDelete }) {
  const stats = c.stats || {};
  const cls = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
  return (
    <motion.div
      layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="border border-[#E0D4B6] bg-[#FFFFFF] p-5 hover:border-[#E8943A]/60 transition cursor-pointer flex items-center gap-4"
      onClick={onOpen}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${cls}`}>{c.status}</span>
          <div className="velora-serif text-lg text-[#3D1F00] truncate">{c.name || "Untitled"}</div>
        </div>
        <div className="text-sm text-[#3D1F00]/60 truncate">{c.subject}</div>
      </div>
      <div className="hidden md:flex items-center gap-4 text-sm">
        <Stat label="Recipients" value={c.recipient_count ?? "-"} />
        <Stat label="Sent" value={stats.sent ?? 0} />
        <Stat label="Opened" value={stats.opened ?? 0} />
        <Stat label="Clicked" value={stats.clicked ?? 0} />
      </div>
      <button onClick={(e) => { e.stopPropagation(); onDelete(c); }} className="text-[#3D1F00]/30 hover:text-[#7B3F00]">
        <Trash2 size={14} />
      </button>
    </motion.div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center min-w-[60px]">
      <div className="velora-serif text-xl text-[#7B3F00] leading-none">{value}</div>
      <div className="velora-micro text-[#3D1F00]/40 mt-1">{label}</div>
    </div>
  );
}

function CampaignDetail({ id, onClose }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("compose"); // compose | recipients | dashboard
  const [testEmail, setTestEmail] = useState("");
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    mailingApi.templates.list().then(setTemplates).catch(() => {});
  }, []);

  const applyTemplate = (t) => {
    if (!t) return;
    if (!window.confirm(`Replace this campaign's content with template "${t.name}"? Recipients and campaign name are kept.`)) return;
    setDraft((d) => ({ ...d, ...templateContent(t) }));
    toast.success(`Applied template "${t.name}"`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await mailingApi.campaigns.get(id);
      setCampaign(c);
      setDraft(c);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh while sending
  useEffect(() => {
    if (campaign?.status === "sending" || campaign?.status === "scheduled") {
      const t = setInterval(load, 5000);
      return () => clearInterval(t);
    }
  }, [campaign?.status, load]);

  const isEditable = campaign && (campaign.status === "draft" || campaign.status === "scheduled");

  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      const c = await mailingApi.campaigns.update(id, draft);
      setCampaign(c); setDraft(c);
      if (!quiet) toast.success("Saved");
      return c;
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
      throw err;
    }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    const email = testEmail.trim();
    if (!email) return toast.error("Enter test email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Enter a valid test email");
    }
    try {
      if (isEditable) await save({ quiet: true });
      await mailingApi.campaigns.testSend(id, email);
      setTestEmail(email);
      toast.success(`Test sent to ${email}`);
      load();
    } catch (err) { toast.error(formatApiError(err, "Test send failed")); }
  };

  const start = async (scheduledFor = null) => {
    const rq = draft?.recipient_query || {};
    const m = rq.mode || (rq.contact_ids?.length ? "specific" : (rq.tags?.length ? "tags" : "all"));
    if (m === "specific" && !(rq.contact_ids || []).length) {
      return toast.error("No recipients selected. Pick contacts in the Recipients tab, or switch to All / By tag.");
    }
    if (m === "tags" && !(rq.tags || []).length) {
      return toast.error("No tags set. Add a tag in the Recipients tab, or switch to All / Specific.");
    }
    const who = m === "specific"
      ? `${rq.contact_ids.length} selected contact${rq.contact_ids.length > 1 ? "s" : ""}`
      : m === "tags"
        ? `contacts tagged: ${rq.tags.join(", ")}`
        : "ALL active contacts";
    if (!window.confirm(scheduledFor ? `Schedule send to ${who} for ${scheduledFor}?` : `Send now to ${who}?`)) return;
    try {
      await save({ quiet: true });
      const c = await mailingApi.campaigns.start(id, scheduledFor);
      setCampaign(c);
      toast.success(scheduledFor ? "Scheduled" : "Sending started");
    } catch (err) { toast.error(formatApiError(err, "Start failed")); }
  };

  const pause = async () => {
    try { setCampaign(await mailingApi.campaigns.pause(id)); }
    catch (err) { toast.error(formatApiError(err, "Pause failed")); }
  };
  const resume = async () => {
    try { setCampaign(await mailingApi.campaigns.resume(id)); }
    catch (err) { toast.error(formatApiError(err, "Resume failed")); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this campaign? Pending recipients will not be emailed.")) return;
    try { setCampaign(await mailingApi.campaigns.cancel(id)); }
    catch (err) { toast.error(formatApiError(err, "Cancel failed")); }
  };

  if (loading || !campaign) {
    return <div className="p-12 text-center velora-micro text-[#3D1F00]/40">Loading…</div>;
  }

  const cls = STATUS_BADGE[campaign.status] || STATUS_BADGE.draft;
  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <button onClick={onClose} className="velora-btn-ghost flex items-center gap-2"><ArrowLeft size={14}/> Back</button>
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${cls}`}>{campaign.status}</span>
          {isEditable ? (
            <input
              value={draft.name || ""}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="velora-input flex-1 max-w-md velora-serif !text-lg"
            />
          ) : (
            <div className="velora-serif text-lg text-[#3D1F00] truncate">{campaign.name}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEditable && (
            <>
              <button onClick={save} disabled={saving} className="velora-btn-ghost flex items-center gap-2">
                {saving ? "Saving..." : "Save"}
              </button>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@you.com"
                className="velora-input"
                style={{ maxWidth: "200px" }}
              />
              <button onClick={testSend} className="velora-btn-ghost flex items-center gap-2">
                <Mail size={14}/> Test
              </button>
              <button onClick={() => start(null)} className="velora-btn-primary flex items-center gap-2">
                <Send size={14}/> Send now
              </button>
            </>
          )}
          {campaign.status === "sending" && (
            <button onClick={pause} className="velora-btn-ghost flex items-center gap-2">
              <Pause size={14}/> Pause
            </button>
          )}
          {campaign.status === "paused" && (
            <button onClick={resume} className="velora-btn-primary flex items-center gap-2">
              <Play size={14}/> Resume
            </button>
          )}
          {(campaign.status === "sending" || campaign.status === "paused" || campaign.status === "scheduled") && (
            <button onClick={cancel} className="velora-btn-ghost !text-[#C0392B] flex items-center gap-2">
              <XIcon size={14}/> Cancel
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-6 border-b border-[#E0D4B6] mb-6">
        {["compose", "recipients", "dashboard"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`py-2 velora-micro border-b-2 -mb-[1px] ${tab === t ? "border-[#E8943A] text-[#7B3F00]" : "border-transparent text-[#3D1F00]/40 hover:text-[#3D1F00]"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "compose" && isEditable && (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="velora-micro text-[#3D1F00]/50 flex items-center gap-1"><FileText size={12}/> Apply a saved template:</span>
            <select
              className="velora-input"
              value=""
              onChange={(e) => { applyTemplate(templates.find((t) => t.id === e.target.value)); e.target.value = ""; }}
            >
              <option value="" disabled>Choose template…</option>
              {templates.length === 0
                ? <option value="" disabled>No templates yet</option>
                : templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <span className="velora-micro text-[#3D1F00]/30">replaces content, keeps recipients</span>
          </div>
          <CampaignEditor value={draft} onChange={setDraft} />
        </>
      )}
      {tab === "compose" && !isEditable && (
        <div className="velora-micro text-[#3D1F00]/50 p-6 border border-[#E0D4B6] bg-[#FFFFFF]">
          Campaign content is locked once sending has started. View the snapshot in the Dashboard tab.
        </div>
      )}
      {tab === "recipients" && (
        <RecipientsTab campaign={campaign} draft={draft} setDraft={setDraft} editable={isEditable}/>
      )}
      {tab === "dashboard" && <DashboardTab id={id} campaign={campaign} />}
    </div>
  );
}

function RecipientsTab({ campaign, draft, setDraft, editable }) {
  const q = (editable ? draft : campaign).recipient_query || {};
  const tagsCsv = (q.tags || []).join(", ");

  const initialMode = q.mode || (q.contact_ids?.length ? "specific" : (q.tags?.length ? "tags" : "all"));
  const [mode, setMode] = useState(initialMode);

  // Replace the whole recipient_query so it only ever carries the keys that
  // match the chosen mode — no stale all_active/tags/contact_ids left behind.
  const setRQ = (rq) => setDraft({ ...draft, recipient_query: rq });

  const changeMode = (m) => {
    setMode(m);
    if (m === "all") setRQ({ mode: "all", all_active: true });
    else if (m === "tags") setRQ({ mode: "tags", all_active: false, tags: q.tags || [] });
    else setRQ({ mode: "specific", all_active: false, contact_ids: q.contact_ids || [] });
  };
  const set = (patch) => setRQ({ mode, all_active: false, ...q, mode, ...patch });

  return (
    <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6 max-w-2xl space-y-4">
      <div>
        <label className="velora-micro text-[#3D1F00]/50 block mb-1">Who receives this campaign?</label>
        <select
          disabled={!editable}
          value={mode}
          onChange={(e) => changeMode(e.target.value)}
          className="velora-input w-full"
        >
          <option value="all">All active contacts</option>
          <option value="tags">By tag(s)</option>
          <option value="specific">Specific contacts (pick from list)</option>
        </select>
      </div>

      {mode === "tags" && (
        <div>
          <label className="velora-micro text-[#3D1F00]/50 block mb-1">Tags (comma-separated, OR match)</label>
          <input
            disabled={!editable}
            value={tagsCsv}
            onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
            className="velora-input w-full"
            placeholder="e.g. importers-2026, ceramics"
          />
        </div>
      )}

      {mode === "specific" && (
        <ContactPicker
          selectedIds={q.contact_ids || []}
          editable={editable}
          onChange={(ids) => set({ contact_ids: ids })}
        />
      )}

      <div className="velora-micro text-[#3D1F00]/40 pt-2 border-t border-[#E0D4B6]/50">
        {mode === "specific" ? (
          <span>Selected recipients: <strong className="text-[#7B3F00]">{(q.contact_ids || []).length}</strong></span>
        ) : mode === "all" ? (
          <span>Sends to <strong className="text-[#7B3F00]">every active contact</strong> (no email / unsubscribed / bounced are skipped).</span>
        ) : (
          <span>Sends to active contacts matching the tag(s) above.</span>
        )}
        {campaign.recipient_count != null && (
          <span className="block mt-1">Recipients in last expansion: <strong className="text-[#7B3F00]">{campaign.recipient_count}</strong></span>
        )}
      </div>
    </div>
  );
}

function ContactPicker({ selectedIds, editable, onChange }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  useEffect(() => {
    setLoading(true);
    mailingApi.contacts.list({ status: "active", limit: 2000 })
      .then(setContacts)
      .catch(() => toast.error("Failed to load contacts"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return contacts;
    const s = search.toLowerCase();
    return contacts.filter((c) =>
      [c.email, c.name, c.company, c.country].some((v) => (v || "").toLowerCase().includes(s)));
  }, [contacts, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange([...next]);
  };
  const selectFiltered = () => onChange([...new Set([...selected, ...filtered.map((c) => c.id)])]);
  const clearAll = () => onChange([]);

  return (
    <div className="border border-[#E0D4B6] bg-[#FAF3E0]">
      <div className="flex items-center gap-2 p-3 border-b border-[#E0D4B6]">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts…"
          className="velora-input flex-1"
        />
        {editable && (
          <>
            <button type="button" onClick={selectFiltered} className="velora-btn-ghost whitespace-nowrap">Select shown</button>
            <button type="button" onClick={clearAll} className="velora-btn-ghost whitespace-nowrap">Clear</button>
          </>
        )}
      </div>
      <div className="max-h-[340px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center velora-micro text-[#3D1F00]/40">Loading contacts…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center velora-micro text-[#3D1F00]/40">No active contacts found.</div>
        ) : (
          filtered.map((c) => (
            <label
              key={c.id}
              className={`flex items-center gap-3 px-4 py-2 border-b border-[#E0D4B6]/40 cursor-pointer ${selected.has(c.id) ? "bg-[#7B3F00]/5" : "hover:bg-[#FFFFFF]/60"}`}
            >
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                onChange={() => toggle(c.id)}
                disabled={!editable}
                className="accent-[#7B3F00] w-4 h-4"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#3D1F00] truncate">{c.company || c.name || c.email || "—"}</div>
                <div className="velora-micro text-[#3D1F00]/45 truncate">{c.email || "no email"}{c.country ? ` · ${c.country}` : ""}</div>
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function DashboardTab({ id, campaign }) {
  const [jobs, setJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mailingApi.campaigns.jobs(id, statusFilter ? { status: statusFilter } : {});
      setJobs(data);
    } finally { setLoading(false); }
  }, [id, statusFilter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (campaign.status === "sending") {
      const t = setInterval(load, 5000);
      return () => clearInterval(t);
    }
  }, [campaign.status, load]);

  const s = campaign.stats || {};
  const total = (s.pending || 0) + (s.sent || 0) + (s.dead || 0);
  const pct = total ? Math.round((((s.sent || 0) + (s.dead || 0)) / total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile label="Recipients" value={campaign.recipient_count ?? 0} icon={<Mail size={12}/>}/>
        <Tile label="Sent" value={s.sent ?? 0} accent/>
        <Tile label="Pending" value={s.pending ?? 0} icon={<Clock size={12}/>}/>
        <Tile label="Dead" value={s.dead ?? 0} icon={<AlertTriangle size={12}/>} danger/>
        <Tile label="Opened" value={s.opened ?? 0} icon={<Eye size={12}/>}/>
        <Tile label="Clicked" value={s.clicked ?? 0} icon={<MousePointerClick size={12}/>}/>
      </div>

      <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="velora-micro text-[#7B3F00]">Progress</div>
          <div className="velora-micro text-[#3D1F00]/40">{pct}%</div>
        </div>
        <div className="h-2 bg-[#FAF3E0] border border-[#E0D4B6]">
          <div className="h-full bg-[#E8943A]" style={{ width: `${pct}%`, transition: "width 1s" }} />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="velora-input">
            <option value="">All jobs</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="dead">Dead</option>
            <option value="locked">Locked</option>
          </select>
          <span className="velora-micro text-[#3D1F00]/40">{loading ? "Loading…" : `${jobs.length} jobs`}</span>
        </div>
        <div className="border border-[#E0D4B6] bg-[#FAF3E0] max-h-[400px] overflow-y-auto">
          {jobs.length === 0 ? (
            <div className="p-8 text-center velora-micro text-[#3D1F00]/40">No jobs.</div>
          ) : (
            jobs.map((j) => (
              <div key={j.id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[#E0D4B6]/40 text-xs">
                <a
                  href={`mailto:${j.email}`}
                  className="col-span-4 truncate hover:text-[#7B3F00] transition-colors"
                  title={j.email}
                >
                  {j.email}
                </a>
                <div className="col-span-2"><JobStatus status={j.status}/></div>
                <div className="col-span-1">a:{j.attempts}</div>
                <div className="col-span-5 truncate text-[#3D1F00]/40">{j.last_error || "-"}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, icon, accent, danger }) {
  const color = danger ? "text-[#C0392B]" : accent ? "text-[#7B3F00]" : "text-[#3D1F00]";
  return (
    <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-4">
      <div className="velora-micro text-[#3D1F00]/50 flex items-center gap-1">{icon} {label}</div>
      <div className={`velora-serif text-3xl ${color} mt-2 leading-none`}>{value}</div>
    </div>
  );
}

function JobStatus({ status }) {
  const map = {
    pending: "text-[#3D1F00]/60",
    locked: "text-[#7B3F00]",
    sent: "text-[#4A6741]",
    dead: "text-[#C0392B]",
  };
  return <span className={map[status] || "text-[#3D1F00]/60"}>{status}</span>;
}
